import { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { tokens } from "../../../theme";
import { closeButtonStyles } from "../../../styles/buttonStyles";
import StatusChip from "../../../components/StatusChip";
import useResizableDrawerWidth from "../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../components/DrawerSnapToggle";
import RepositoryConnectionCard from "./RepositoryConnectionCard";
import EndpointsAccessCard from "./EndpointsAccessCard";
import SchedulesCard from "./SchedulesCard";
import InfrastructureCard from "./InfrastructureCard";
import HistoryCard from "./HistoryCard";
import { drawerStyles as styles } from "./styles";
import { sentenceCase } from "./utils";

// Read-only companion to the editable members/schedule drawer. Reads
// straight off the already-loaded row (structuredClone in getEnvsSimplified
// keeps every raw backend field) — no fetch, no loading state.
const DetailsDrawer = ({ envId, envs, onClose, currentUserEmail }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "environment-readonly-details",
    defaultWidth: 580,
    maxWidth: 900,
    enabled: isDesktopDrawer,
  });

  const liveEnv = envs?.find((env) => env?.id === envId);
  const [lastKnownEnv, setLastKnownEnv] = useState(null);

  useEffect(() => {
    if (liveEnv) setLastKnownEnv(liveEnv);
  }, [liveEnv]);

  useEffect(() => {
    if (!envId) setLastKnownEnv(null);
  }, [envId]);

  const open = Boolean(envId);
  const displayEnv = liveEnv || lastKnownEnv;
  // envs.length > 0 means the list has loaded at least once, so a missing id
  // really means the environment is gone (not just "not fetched yet").
  const unavailable = Boolean(envId) && !liveEnv && (envs?.length || 0) > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { ...styles.paper(colors, drawerWidth), overflow: "visible" },
      }}
    >
      <DrawerResizeHandle
        {...resizeHandleProps}
        ariaLabel="Resize environment overview drawer"
      />
      <DrawerSnapToggle
        enabled={isDesktopDrawer}
        isWide={isDrawerWide}
        onClick={toggleDrawerWide}
        ariaLabel="Expand environment overview drawer to 75% width"
        collapseLabel="Collapse environment overview drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      {displayEnv && (
        <>
          <Box sx={styles.header(colors)}>
            <Box sx={styles.headerTopRow}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={styles.headerTitleRow}>
                  <Typography sx={styles.headerName(colors)}>
                    {displayEnv.name}
                  </Typography>
                  <StatusChip value={displayEnv.status} />
                </Box>
                {displayEnv.description && (
                  <Typography sx={styles.headerDescription(colors)}>
                    {displayEnv.description}
                  </Typography>
                )}
                <Box sx={styles.headerMeta(colors)}>
                  {displayEnv.url && (
                    <Typography
                      component="a"
                      href={`https://${displayEnv.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={styles.headerMetaLink(colors)}
                    >
                      {displayEnv.url}
                    </Typography>
                  )}
                  {displayEnv.release_label && (
                    <>
                      <Box
                        component="span"
                        sx={styles.headerMetaSeparator(colors)}
                      >
                        ·
                      </Box>
                      <Box component="span" sx={styles.headerMetaMono}>
                        {displayEnv.release_label}
                      </Box>
                    </>
                  )}
                  {displayEnv.purpose_type && (
                    <>
                      <Box
                        component="span"
                        sx={styles.headerMetaSeparator(colors)}
                      >
                        ·
                      </Box>
                      <span>{sentenceCase(displayEnv.purpose_type)}</span>
                    </>
                  )}
                  {displayEnv.meta?.deployment_stage && (
                    <>
                      <Box
                        component="span"
                        sx={styles.headerMetaSeparator(colors)}
                      >
                        ·
                      </Box>
                      <span>
                        {sentenceCase(displayEnv.meta.deployment_stage)}
                      </span>
                    </>
                  )}
                </Box>
              </Box>
              <IconButton
                sx={closeButtonStyles(colors)}
                onClick={onClose}
                title="Hide details"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={styles.body(colors)}>
            {unavailable ? (
              <Box sx={styles.unavailableBanner(colors)}>
                This environment is no longer available.
              </Box>
            ) : (
              <>
                <RepositoryConnectionCard row={displayEnv} />
                <EndpointsAccessCard
                  row={displayEnv}
                  currentUserEmail={currentUserEmail}
                />
                <SchedulesCard row={displayEnv} />
                <InfrastructureCard row={displayEnv} />
                <HistoryCard row={displayEnv} />
              </>
            )}
          </Box>

          <Box sx={styles.footer(colors)}>
            View only · actions stay on the table row
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default DetailsDrawer;
